import { hyperliquidWalletProvider } from './providers/hyperliquid'
import { kalshiWalletProvider } from './providers/kalshi'
import { limitlessWalletProvider } from './providers/limitless'
import { myriadWalletProvider } from './providers/myriad'
import { polymarketWalletProvider } from './providers/polymarket'
import { predictfunWalletProvider } from './providers/predictfun'
import {
	type BridgePlatform,
	type MarketWalletProvider,
	PLATFORMS,
	type PlatformChainConfig,
	type TransferDirection,
	type WalletRuntime,
} from './types'

export { PLATFORMS }
export type { BridgePlatform, TransferDirection }

export const WALLET_PROVIDERS = [
	polymarketWalletProvider,
	kalshiWalletProvider,
	predictfunWalletProvider,
	limitlessWalletProvider,
	myriadWalletProvider,
	hyperliquidWalletProvider,
] as const satisfies readonly MarketWalletProvider[]

export const WALLET_PROVIDER_BY_PLATFORM = Object.fromEntries(
	WALLET_PROVIDERS.map(provider => [provider.id, provider])
) as Record<BridgePlatform, MarketWalletProvider>

export const CHAIN_CONFIG_BY_PLATFORM = Object.fromEntries(
	WALLET_PROVIDERS.map(provider => [provider.id, provider.chain])
) as Record<BridgePlatform, PlatformChainConfig>

export function isPlatform(value: unknown): value is BridgePlatform {
	return typeof value === 'string' && PLATFORMS.includes(value as BridgePlatform)
}

export function getWalletProvider(platform: BridgePlatform): MarketWalletProvider {
	return WALLET_PROVIDER_BY_PLATFORM[platform]
}

export function getMarketWalletAddress(
	platform: BridgePlatform,
	runtime: WalletRuntime
): string | undefined {
	return getWalletProvider(platform).resolveAddress(runtime)
}

export function getPlatformAddress(
	platform: BridgePlatform,
	runtime: WalletRuntime
): string | undefined {
	return getMarketWalletAddress(platform, runtime)
}

export function getPlatformDecimals(platform: BridgePlatform): number {
	return getWalletProvider(platform).decimals
}

export function getTransferDirection(platform: BridgePlatform): TransferDirection {
	return getWalletProvider(platform).direction
}

export function getPlatformChainConfig(platform: BridgePlatform): PlatformChainConfig {
	return getWalletProvider(platform).chain
}

export function getMarketBalanceQueryKey(
	platform: BridgePlatform,
	address: string | undefined
): readonly unknown[] {
	return getWalletProvider(platform).balanceQueryKey(address)
}
